/* eslint-disable consistent-return */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
// @material-ui/icons
import ArrowDown from '@material-ui/icons/ArrowDownward';
import ArrowUp from '@material-ui/icons/ArrowUpward';
// @material-ui/core components
import FilterStyles from 'assets/jss/AdsFilter';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import Select from '@material-ui/core/Select/Select';
import withStyles from '@material-ui/core/styles/withStyles';
// global variables
import { reviewReportReasons, reviewReportStatus } from 'config';
// core components
import Button from 'components/UI/CustomButtons/Button';
import Card from 'components/UI/Card/Card';
import CardBody from 'components/UI/Card/CardBody';
import CustomInput from 'components/UI/CustomInput/CustomInput';
import GridContainer from 'components/UI/Grid/GridContainer';
import GridItem from 'components/UI/Grid/GridItem';
// our components
import Datepicker from 'components/common/DatePicker';
import SectionDivider from 'components/common/SectionDivider/SectionDivider';

const singleValueArray = value => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  return Array.isArray(value) ? value : [value];
};

const defaultFilter = {
  status: [reviewReportStatus.OPEN],
};

class ReviewReportFilter extends React.Component {
  constructor(props) {
    super(props);
    const { appliedFilter } = props;
    this.state = {
      localFilter: appliedFilter || {},
      expandFiltersAnimation: false,
    };
  }

  /**
   * @description
   * Change filters.
   *
   * @param {Object} changedFilter - Changed filters values.
   */
  handleChangeFilter = changedFilter => {
    const { localFilter } = this.state;
    const { filter } = this.props;
    const filters = { ...localFilter, ...changedFilter };
    this.setState({ localFilter: filters });
    filter(filters);
  };

  /**
   * @description
   * Function to expand animation.
   *
   * @returns {undefined} - No return value.
   */
  expandAnimation = () => {
    const { expandFiltersAnimation } = this.state;
    const { showFilters } = this.props;

    this.setState({ expandFiltersAnimation: !expandFiltersAnimation }, () =>
      setTimeout(showFilters, 150),
    );
  };

  renderTextFilter = ({ id, placeholder, value }) => {
    const { classes, t } = this.props;
    const { localFilter } = this.state;
    return (
      <CustomInput
        id={id}
        formControlProps={{
          className: `${classes.top} ${classes.search}`,
        }}
        inputProps={{
          placeholder: t(placeholder),
          onChange: e => {
            const nextValue = e.target.value;
            this.handleChangeFilter({
              [id]: nextValue !== '' ? nextValue : undefined,
            });
          },
          value: localFilter[value || id] || '',
          type: 'text',
        }}
      />
    );
  };

  renderDateFilter = ({ id, title, validGte, validLte, startOfDay }) => {
    const { classes, t } = this.props;
    const { localFilter } = this.state;
    const minDate = validGte ? localFilter[validGte] : undefined;
    const maxDate = validLte ? localFilter[validLte] : undefined;
    return (
      <Datepicker
        id={id}
        title={t(title)}
        defaultValue={t(title)}
        onChange={date => {
          if (!date) {
            this.handleChangeFilter({ [id]: null });
            return;
          }
          const selectedDate = moment(date);
          if (
            validGte &&
            localFilter[validGte] &&
            selectedDate.isBefore(moment(localFilter[validGte]))
          ) {
            return;
          }
          if (
            validLte &&
            localFilter[validLte] &&
            selectedDate.isAfter(moment(localFilter[validLte]))
          ) {
            return;
          }
          const nextDate = selectedDate.clone();
          if (startOfDay) {
            nextDate.startOf('day');
          } else {
            nextDate.endOf('day');
          }
          this.handleChangeFilter({
            [id]: nextDate.format(),
          });
        }}
        value={
          localFilter[id] !== undefined && localFilter[id] !== null
            ? moment(localFilter[id])
            : ''
        }
        toToday
        validGte={maxDate}
        validLte={minDate}
        className={classes.search}
      />
    );
  };

  renderCollapsedFilters() {
    const { expandFiltersAnimation } = this.state;
    const { classes, t } = this.props;
    return (
      <div
        className={`${classes.animationFilter}
      ${
        expandFiltersAnimation
          ? classes.expandCollapsedFilters
          : classes.collapseCollapsedFilters
      }`}
      >
        <GridContainer>
          <GridItem xs={12} style={{ textAlign: 'center' }}>
            <Button
              color="primary"
              simple
              className={classes.showFilters}
              onClick={this.expandAnimation}
              id="showFilters"
              key="showFilters"
              fullWidth
            >
              <ArrowDown />
              {t('review_report.filters.show')}
            </Button>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  renderFilters() {
    const { expandFiltersAnimation, localFilter } = this.state;
    const { classes, t, restartFilters } = this.props;
    const status = singleValueArray(localFilter.status);
    const reason = singleValueArray(localFilter.reason);
    return (
      <div
        className={`${classes.animationFilter}
      ${
        expandFiltersAnimation ? classes.expandFilters : classes.collapseFilters
      }`}
      >
        <GridContainer>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('review_report.filters.section.ids')}>
              <GridContainer>
                <GridItem xs={12} sm={6}>
                  {this.renderTextFilter({
                    id: 'id',
                    placeholder: 'review_report.filters.id_placeholder',
                  })}
                </GridItem>
                <GridItem xs={12} sm={6}>
                  {this.renderTextFilter({
                    id: 'review',
                    placeholder: 'review_report.filters.review_placeholder',
                  })}
                </GridItem>
                <GridItem xs={12} sm={6}>
                  {this.renderTextFilter({
                    id: 'reporter',
                    placeholder: 'review_report.filters.reporter_placeholder',
                  })}
                </GridItem>
                <GridItem xs={12} sm={6}>
                  {this.renderTextFilter({
                    id: 'reviewer',
                    placeholder: 'review_report.filters.reviewer_placeholder',
                  })}
                </GridItem>
                <GridItem xs={12} sm={6}>
                  {this.renderTextFilter({
                    id: 'reviewee',
                    placeholder: 'review_report.filters.reviewee_placeholder',
                  })}
                </GridItem>
                <GridItem xs={12} sm={6}>
                  {this.renderTextFilter({
                    id: 'reviewedBy',
                    placeholder:
                      'review_report.filters.reviewed_by_placeholder',
                  })}
                </GridItem>
              </GridContainer>
            </SectionDivider>
          </GridItem>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('review_report.filters.section.status')}>
              <div className={classes.responsiveHorizontalScroll}>
                <Button
                  color="info"
                  round
                  simple={status !== undefined}
                  onClick={() => this.handleChangeFilter({ status: undefined })}
                  id="filterStatusAll"
                >
                  {t('common.all')}
                </Button>
                {Object.keys(reviewReportStatus).map(key => (
                  <Button
                    color="info"
                    round
                    simple={
                      !status || status.indexOf(reviewReportStatus[key]) === -1
                    }
                    onClick={() =>
                      this.handleChangeFilter({
                        status: [reviewReportStatus[key]],
                      })
                    }
                    id={`filterStatus${key}`}
                    key={`filterStatus${key}`}
                  >
                    {t(`review_report.status.${reviewReportStatus[key]}`)}
                  </Button>
                ))}
              </div>
            </SectionDivider>
            <SectionDivider label={t('review_report.filters.section.reason')}>
              <FormControl fullWidth className={classes.filterSelect}>
                <Select
                  id="reason"
                  MenuProps={{
                    className: classes.selectMenu,
                  }}
                  classes={{
                    select: classes.select,
                  }}
                  name="reason"
                  onChange={event => {
                    this.handleChangeFilter({
                      reason: event.target.value
                        ? [event.target.value]
                        : undefined,
                    });
                  }}
                  value={reason && reason.length ? reason[0] : ''}
                  className={classes.reviewDD}
                  displayEmpty
                >
                  <MenuItem
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                    value=""
                  >
                    {t('common.all')}
                  </MenuItem>
                  {Object.keys(reviewReportReasons).map(key => (
                    <MenuItem
                      key={reviewReportReasons[key]}
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected,
                      }}
                      value={reviewReportReasons[key]}
                    >
                      {t(`review_report.reasons.${reviewReportReasons[key]}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </SectionDivider>
          </GridItem>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('review_report.filters.section.created')}>
              <GridContainer>
                <GridItem xs={12} sm={6}>
                  {this.renderDateFilter({
                    id: 'createdOnGte',
                    title: 'review_report.filters.date_from',
                    validLte: 'createdOnLte',
                    startOfDay: true,
                  })}
                </GridItem>
                <GridItem xs={12} sm={6}>
                  {this.renderDateFilter({
                    id: 'createdOnLte',
                    title: 'review_report.filters.date_to',
                    validGte: 'createdOnGte',
                  })}
                </GridItem>
              </GridContainer>
            </SectionDivider>
          </GridItem>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('review_report.filters.section.reviewed')}>
              <GridContainer>
                <GridItem xs={12} sm={6}>
                  {this.renderDateFilter({
                    id: 'reviewedOnGte',
                    title: 'review_report.filters.date_from',
                    validLte: 'reviewedOnLte',
                    startOfDay: true,
                  })}
                </GridItem>
                <GridItem xs={12} sm={6}>
                  {this.renderDateFilter({
                    id: 'reviewedOnLte',
                    title: 'review_report.filters.date_to',
                    validGte: 'reviewedOnGte',
                  })}
                </GridItem>
              </GridContainer>
            </SectionDivider>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={6}>
            <Button
              color="primary"
              simple
              className={classes.buttons}
              onClick={this.expandAnimation}
              id="hideFilters"
              key="hideFilters"
            >
              <ArrowUp />
              {t('review_report.filters.hide')}
            </Button>
          </GridItem>
          <GridItem xs={12} sm={6}>
            <GridContainer justify="flex-end">
              <GridItem>
                <Button
                  color="danger"
                  simple
                  className={classes.buttons}
                  onClick={() => {
                    this.setState({ localFilter: { ...defaultFilter } });
                    restartFilters();
                  }}
                  id="restartFilters"
                  key="restartFilters"
                >
                  {t('review_report.filters.clean')}
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  render() {
    const { showCollapseFilters } = this.props;
    return (
      <Card>
        <CardBody>
          {!showCollapseFilters
            ? this.renderFilters()
            : this.renderCollapsedFilters()}
        </CardBody>
      </Card>
    );
  }
}

ReviewReportFilter.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  showCollapseFilters: PropTypes.bool.isRequired,
  showFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withStyles(FilterStyles)(ReviewReportFilter));
